import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const POSTS = [
  {
    id: "not-more-strategy",
    cat: "Field note",
    date: "Dec 2025",
    read: "6 min",
    title: "The climate transition doesn't need more strategy. It needs more finishers.",
    excerpt:
      "Most transition capital disappears in the gap between PowerPoint and commissioning. Here's the model we've used across 17 engagements to keep it from dying in that gap.",
    body:
      "Over the last decade, the climate-industrial agenda has not suffered from a lack of strategy. It has suffered from a lack of finishers. The sector is crowded with advisors who leave at the feasibility stage and financiers who hand over once the structure is signed. In the middle — the owners-engineer, the commissioning lead, the person who owns the plant on day 91 — there is often a vacuum. Saafi was designed to live inside that vacuum.",
  },
  {
    id: "waste-as-feedstock",
    cat: "Sector brief",
    date: "Nov 2025",
    read: "9 min",
    title: "Waste is the cheapest feedstock we keep pretending is garbage.",
    excerpt:
      "Waste-to-value economics are no longer contingent on subsidies in most emerging markets. What they are contingent on: offtake, logistics and discipline.",
    body:
      "In most of the geographies we operate in, the policy ambiguity around organic and agri-residue streams has resolved into a simple commercial picture: clean feedstock is available, logistics are solvable, and the economics stand up on their own. The winners in this decade will be the platforms that industrialise collection and offtake — not the ones with the cleanest pitch decks.",
  },
  {
    id: "operators-grade-capital",
    cat: "Capital",
    date: "Oct 2025",
    read: "7 min",
    title: "What 'operator-grade capital' actually means (and why most climate funds don't have it).",
    excerpt:
      "There's a version of climate capital that understands term sheets but has never signed a construction contract. That gap is usually where returns die.",
    body:
      "Operator-grade capital is a posture, not a product. It means the cap-table partner has been inside a control room. It means the investment committee asks about commissioning delay probability, not just LCOE. It means the capital partner treats OPEX as fundamental, not a footnote. In our experience, that is still the rarest thing in the market.",
  },
  {
    id: "industrial-decarb-reality",
    cat: "Industry",
    date: "Sep 2025",
    read: "11 min",
    title: "Industrial decarbonisation is a ten-year manufacturing problem, not a pledge.",
    excerpt:
      "Pledges don't electrify a kiln. Here's a more honest sequence — from energy intensity, to electrification, to carbon — that actually moves the metric.",
    body:
      "The heavy-industry operators we work with have stopped asking us about pledges and started asking about sequencing: which intervention, in which year, at which cost curve. The answer is almost never the most ambitious one — it's the one that preserves cash flow and keeps the asset running while the stack is rebuilt under it.",
  },
];

export default function Insights() {
  const [active, setActive] = useState(POSTS[0].id);
  const featured = POSTS.find((p) => p.id === active) || POSTS[0];

  return (
    <div data-testid="insights-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          Insights
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          Field notes from the <em className="not-italic text-[#0F7A3E]">execution</em> edge.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#575B57] leading-relaxed">
          We write when we have something we've seen ourselves. No literature
          reviews, no hot takes — dispatches from the operating rooms, sites
          and boardrooms where the transition is actually being built.
        </p>
      </section>

      {/* Featured + list */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-[#1A1C1A] pt-10">
          <div className="md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
              Featured — {featured.cat} · {featured.date} · {featured.read}
            </p>
            <h2
              data-testid="featured-post-title"
              className="font-display text-3xl md:text-5xl leading-tight tracking-tight font-medium text-[#1A1C1A]"
            >
              {featured.title}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#575B57] max-w-2xl">
              {featured.excerpt}
            </p>
            <p className="mt-6 text-base leading-relaxed text-[#1A1C1A] max-w-2xl">
              {featured.body}
            </p>
          </div>

          <aside className="md:col-span-4 md:col-start-9">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              Archive
            </p>
            <ul className="divide-y divide-[#E4E5DF] border-y border-[#E4E5DF]">
              {POSTS.map((p) => {
                const isActive = p.id === active;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => setActive(p.id)}
                      data-testid={`insight-item-${p.id}`}
                      className={`w-full text-left py-5 group flex items-start gap-4 transition-colors ${
                        isActive ? "text-[#1A1C1A]" : "text-[#575B57] hover:text-[#1A1C1A]"
                      }`}
                    >
                      <span className="font-mono-alt text-[11px] mt-1 w-16 shrink-0">
                        {p.date}
                      </span>
                      <span className={`font-display text-lg leading-snug tracking-tight ${isActive ? "underline underline-offset-[6px] decoration-[#0F7A3E]" : ""}`}>
                        {p.title}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F3F3E8]/60 border-y border-[#E4E5DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              Questions
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium">
              What operators and investors ask us first.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6" data-testid="insights-faq">
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: "Do you only work with large corporates?",
                  a: "No. We partner with large operators and institutional investors, but also with founders and platforms where the thesis and execution risk are genuinely interesting.",
                },
                {
                  q: "How is Saafi different from a traditional consulting firm?",
                  a: "Our compensation is frequently tied to delivery. We invest our own balance sheet alongside clients. And our default end-state is a built, operating asset — not a report.",
                },
                {
                  q: "Where do you operate geographically?",
                  a: "Our active engagements are concentrated in India, with selective cross-border work where our partners need us. We travel to where the decisions are being made.",
                },
                {
                  q: "Can you co-invest with our fund / DFI?",
                  a: "Yes. Many of our best engagements are alongside aligned capital partners — including development finance institutions and strategic LPs.",
                },
              ].map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#1A1C1A]/30">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display text-xl md:text-2xl tracking-tight py-6 hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[#575B57] pb-8 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
